<template>
  <client-only>
    <div class="tw-flex tw-flex-col">
      <lazy-library-text :text="`${label}${isRequired ? '*' : ''}`" tag="span" type="input-label"></lazy-library-text>
      <date-picker
        v-model="childModelValue"
        :mode="mode"
        :masks="dateMasks"
        :name="name"
        :hide-time-header="false"
        is24hr
        :is-required="isRequired"
        :select-attribute="selectAttribute"
        :is-dark="isDarkMode"
      >
      </date-picker>
    </div>
  </client-only>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';

export default defineNuxtComponent({
  name: 'LibraryDatePicker',
  components: { DatePicker },

  props: {
    modelValue: {
      required: true,
      type: [Date, String, Number, Object] as PropType<Date | string | number | any>,
    },

    dateMasks: {
      required: false,
      type: Object,
      default: () => {
        return {
          input: 'DD/MM/YYYY',
        };
      },
    },

    name: {
      required: false,
      type: String,
      default: '',
    },

    label: {
      required: false,
      type: String,
      default: '',
    },

    isRequired: {
      required: false,
      type: Boolean,
      default: false,
    },

    mode: {
      required: false,
      type: String as PropType<'date'>,
      default: 'date',
    },
  },

  data() {
    return {
      isDarkMode: false,
      themeObserver: null as MutationObserver | null,
    };
  },

  computed: {
    childModelValue: {
      get() {
        return this.modelValue;
      },
      set(value: string) {
        this.$emit('update:modelValue', value);
      },
    },

    selectAttribute() {
      return {
        highlight: {
          style: {
            // Circle styles using CSS variables
            background: 'var(--c-primary)',
          },
          contentStyle: {
            // Text styles
            color: 'white',
          },
        },
      };
    },
  },

  methods: {
    detectTheme() {
      // Check localStorage first
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        this.isDarkMode = savedTheme === 'dark';
        return;
      }

      // Fallback to HTML class detection
      this.isDarkMode = !document.documentElement.classList.contains('light-mode');
    },

    setupThemeObserver() {
      // Watch for changes in HTML class
      this.themeObserver = new MutationObserver(() => {
        this.detectTheme();
      });

      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });

      // Watch for localStorage changes (from other tabs/windows)
      window.addEventListener('storage', (e) => {
        if (e.key === 'theme') {
          this.detectTheme();
        }
      });
    },
  },

  mounted() {
    this.detectTheme();
    this.setupThemeObserver();
  },

  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    window.removeEventListener('storage', this.detectTheme);
  },
});
</script>

<style>
/* Custom CSS variables for v-calendar theming */
.vc-container {
  --vc-accent-50: var(--c-primary-50, #eff6ff);
  --vc-accent-100: var(--c-primary-100, #dbeafe);
  --vc-accent-200: var(--c-primary-200, #bfdbfe);
  --vc-accent-300: var(--c-primary-300, #93c5fd);
  --vc-accent-400: var(--c-primary-400, #60a5fa);
  --vc-accent-500: var(--c-primary, #3b82f6);
  --vc-accent-600: var(--c-primary-600, #2563eb);
  --vc-accent-700: var(--c-primary-700, #1d4ed8);
  --vc-accent-800: var(--c-primary-800, #1e40af);
  --vc-accent-900: var(--c-primary-900, #1e3a8a);
}

/* Dark mode styles */
.vc-container.vc-dark {
  --vc-bg: var(--c-bg-dark, #1f2937);
  --vc-border: var(--c-border-dark, #374151);
  --vc-color: var(--c-text-dark, #f9fafb);
}

/* Light mode styles */
.vc-container.vc-light {
  --vc-bg: var(--c-bg-light, #ffffff);
  --vc-border: var(--c-border-light, #e5e7eb);
  --vc-color: var(--c-text-light, #111827);
}
</style>
